import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type { DocumentKind, DocumentRow } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';
import { deletePersistedFile } from '@/lib/files';

export async function listDocumentsForSystem(
  db: SQLiteDatabase,
  systemId: string
): Promise<DocumentRow[]> {
  return await db.getAllAsync<DocumentRow>(
    `SELECT * FROM document
     WHERE system_id = ? AND deleted_at IS NULL
     ORDER BY kind, created_at DESC`,
    systemId
  );
}

export async function listDocumentsForProperty(
  db: SQLiteDatabase,
  propertyId: string
): Promise<DocumentRow[]> {
  return await db.getAllAsync<DocumentRow>(
    `SELECT * FROM document
     WHERE property_id = ? AND deleted_at IS NULL
     ORDER BY created_at DESC`,
    propertyId
  );
}

export async function getDocumentById(
  db: SQLiteDatabase,
  id: string
): Promise<DocumentRow | null> {
  return await db.getFirstAsync<DocumentRow>(
    `SELECT * FROM document WHERE id = ? AND deleted_at IS NULL`,
    id
  );
}

export interface NewDocumentInput {
  propertyId: string;
  systemId?: string | null;
  taskId?: string | null;
  kind: DocumentKind;
  title: string;
  file_uri: string;
  mime_type?: string | null;
  file_size?: number | null;
  expires_at?: number | null;
  vendor?: string | null;
  cost_cents?: number | null;
  notes?: string | null;
}

export async function addDocument(
  db: SQLiteDatabase,
  input: NewDocumentInput
): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO document (
      id, property_id, system_id, task_id, kind, title, file_uri,
      mime_type, file_size, expires_at, vendor, cost_cents, notes,
      created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.systemId ?? null,
    input.taskId ?? null,
    input.kind,
    input.title.trim(),
    input.file_uri,
    input.mime_type ?? null,
    input.file_size ?? null,
    input.expires_at ?? null,
    input.vendor ?? null,
    input.cost_cents ?? null,
    input.notes ?? null,
    t,
    t
  );
  bumpDataRevision();
  return id;
}

/**
 * Bulk re-file: set the kind on many documents at once (Wave 49 —
 * powers the Files page's select-mode "Re-file" action so a mis-sorted
 * Smart Scan batch is a three-tap fix).
 */
export async function setDocumentsKind(
  db: SQLiteDatabase,
  ids: string[],
  kind: DocumentKind
): Promise<void> {
  if (ids.length === 0) return;
  const t = now();
  for (const id of ids) {
    await db.runAsync(
      `UPDATE document SET kind = ?, updated_at = ? WHERE id = ?`,
      kind,
      t,
      id
    );
  }
  bumpDataRevision();
}

export async function deleteDocument(
  db: SQLiteDatabase,
  id: string
): Promise<void> {
  const row = await getDocumentById(db, id);
  const t = now();
  await db.runAsync(
    `UPDATE document SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
  // Best-effort blob delete to reclaim space.
  if (row?.file_uri) {
    await deletePersistedFile(row.file_uri).catch(() => {});
  }
  bumpDataRevision();
}

/**
 * Per-property storage cap. Keeps a single Homestead install from growing
 * into a personal cloud — the user can always free space by deleting old
 * files, and we cap at 1 GB so the device's SQLite/OPFS storage stays
 * predictable. The Media & Files page exposes a progress meter pegged
 * against this constant.
 */
export const STORAGE_LIMIT_BYTES = 1024 * 1024 * 1024; // 1 GB

/**
 * Sum of `file_size` across non-deleted documents for the property.
 * Documents with a null file_size contribute 0 — we can't bill what
 * we don't know about, and the typical sources (DocumentPicker,
 * ImagePicker) report size reliably. Returns 0 for unknown property.
 */
export async function getTotalStorageBytes(
  db: SQLiteDatabase,
  propertyId: string
): Promise<number> {
  const row = await db.getFirstAsync<{ total: number | null }>(
    `SELECT COALESCE(SUM(file_size), 0) AS total
       FROM document
      WHERE property_id = ? AND deleted_at IS NULL`,
    propertyId
  );
  return Math.max(0, row?.total ?? 0);
}

/**
 * Human-readable byte formatter. Returns short strings — "234 MB",
 * "1.2 GB", "47 KB". Uses binary multiples (1024) since we measure
 * against an OPFS/disk-style cap, not network throughput.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const formatted = value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2);
  return `${formatted} ${units[i]}`;
}

export const DOCUMENT_KIND_LABEL: Record<DocumentKind, string> = {
  manual: 'Manual',
  warranty: 'Warranty',
  receipt: 'Receipt',
  photo: 'Photo',
  insurance: 'Insurance',
  permit: 'Permit',
  inspection: 'Inspection',
  invoice: 'Invoice',
  estimate: 'Estimate',
  other: 'Other',
};

/**
 * Descriptive title + metadata for a scanned document (Wave 52.1).
 * The document table has structured vendor / cost_cents / notes
 * columns — scans should fill them so Files can show WHAT a file is
 * ("site services — Reputation Management · $773") instead of a bare
 * "Invoice — vendor".
 */
export interface ScanDocMeta {
  title: string;
  vendor: string | null;
  cost_cents: number | null;
  notes: string | null;
}

export function composeScanDocMeta(
  parsed: {
    vendor: string | null;
    description: string | null;
    amount_cents: number | null;
  } | null,
  fallbackTitle: string
): ScanDocMeta {
  const vendor = parsed?.vendor?.trim() || null;
  const desc = parsed?.description?.trim() || null;
  const title =
    vendor && desc && vendor.toLowerCase() !== desc.toLowerCase()
      ? `${vendor} — ${desc}`
      : vendor || desc || fallbackTitle;
  return {
    title: title.slice(0, 80),
    vendor,
    cost_cents: parsed?.amount_cents ?? null,
    notes: desc,
  };
}

/**
 * Filename-based kind guess (Wave 50) — the ONE place upload paths
 * classify a file by its name. Both the Smart Scan PDF flow and the
 * Files uploader use it, so "Quote-11.pdf" lands as an Estimate
 * everywhere instead of "manual" in one place and "other" in another.
 * Order matters: the more specific money words beat "manual".
 */
export function guessDocKindFromFilename(name: string | null | undefined): DocumentKind {
  const n = (name ?? '').toLowerCase();
  if (/quote|estimate|proposal|\bbid\b/.test(n)) return 'estimate';
  if (/invoice|\bbill\b/.test(n)) return 'invoice';
  if (/receipt/.test(n)) return 'receipt';
  if (/warrant/.test(n)) return 'warranty';
  if (/insur|policy|declaration/.test(n)) return 'insurance';
  if (/permit|occupancy|zoning/.test(n)) return 'permit';
  if (/inspect/.test(n)) return 'inspection';
  if (/manual|instruction|guide/.test(n)) return 'manual';
  if (/\.(jpe?g|png|heic|webp|gif)$/.test(n)) return 'photo';
  return 'other';
}
